import { LanguageCode } from '@navio/nsdk';
import { invalidateQueryExactMatch, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessage, LoadingScreen, NoddiBasicCard, useAppForm } from 'noddi-ui-web';
import { commonTexts, isPhoneNumberValid } from 'noddi-util';
import { type ReactNode, useEffect } from 'react';
import { z } from 'zod';
import { useAuthContext } from '../../contexts/useAuthContext';
import { TanstackLanguageCodeSelect } from '../../form/tanstack/features/Language/TanstackLanguageCodeSelect';
import { MfaManagement } from './MfaManagement';
import { PasskeyManagement } from './PasskeyManagement';
import { PasswordChange } from './PasswordChange';
import { RecoveryCodesManagement } from './RecoveryCodesManagement';

const accountValidationSchema = (requireContact: boolean, requiredMessage: string) =>
  z.object({
    firstName: z.string().min(1, 'Required'),
    lastName: z.string().min(1, 'Required'),
    email: requireContact ? z.email(requiredMessage).min(1, requiredMessage) : z.string(),
    phoneNumber: requireContact
      ? z
          .string()
          .min(1, requiredMessage)
          .refine((value) => isPhoneNumberValid(value), requiredMessage)
      : z.string(),
    languageCode: z.enum(LanguageCode)
  });

type FormValues = z.input<ReturnType<typeof accountValidationSchema>>;

interface AccountProps {
  translations?: {
    profileUpdatedSuccessfully: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    save: string;
    language: string;
    validation: {
      required: string;
    };
  };
  onSuccess?: () => void;
  /** When false, hides the password change card (e.g. customer temporarily). Default true. */
  enablePasswordChange?: boolean;
  /** Customer app: saving the profile requires both a phone number and an email. */
  requireContactDetails?: boolean;
  emailAction?: ReactNode;
  phoneNumberAction?: ReactNode;
}

export const Account = ({
  translations,
  onSuccess,
  enablePasswordChange = true,
  requireContactDetails = false,
  emailAction,
  phoneNumberAction
}: AccountProps) => {
  const { userData } = useAuthContext();

  const texts = translations ?? {
    profileUpdatedSuccessfully: commonTexts.profileUpdatedSuccessfully,
    firstName: commonTexts.firstName,
    lastName: commonTexts.lastName,
    email: commonTexts.email,
    phoneNumber: commonTexts.phoneNumber,
    language: commonTexts.language,
    save: commonTexts.save,
    validation: {
      required: commonTexts.required
    }
  };

  const {
    data: user,
    error: userError,
    isPending: isUserPending
  } = noddiAsync.useGet({
    type: URLKeys.getAccount,
    input: { userId: userData?.user.id as number },
    queryConfig: {
      enabled: !!userData?.user.id
    }
  });

  const { mutateAsync: updateUser } = noddiAsync.usePatch({
    type: URLKeys.patchAccount,
    queryConfig: {
      onSuccess: async () => {
        invalidateQueryExactMatch({ urlKey: URLKeys.getAccount, input: { userId: userData?.user.id as number } });
        onSuccess?.();
      }
    }
  });

  if (!userData || isUserPending) {
    return <LoadingScreen />;
  }

  if (userError) {
    return <ApiErrorMessage error={[userError]} />;
  }

  if (!user) {
    return <LoadingScreen />;
  }

  const defaultValues: FormValues = {
    email: user.email ?? '',
    firstName: user.firstName,
    lastName: user.lastName,
    phoneNumber: user.phoneNumber ?? '',
    languageCode: user.languageCode
  };

  return (
    <div className='flex flex-col gap-6'>
      <AccountForm
        defaultValues={defaultValues}
        texts={texts}
        requireContactDetails={requireContactDetails}
        requiredMessage={texts.validation.required}
        emailAction={emailAction}
        phoneNumberAction={phoneNumberAction}
        onSubmit={(values) =>
          updateUser({
            userId: user.id,
            firstName: values.firstName,
            lastName: values.lastName,
            languageCode: values.languageCode,
            // Contact edits go through their own dialogs. Omitting them here
            // keeps a name/language save from clearing a number the dialog just stored.
            // '' is bad data — an empty field clears the stored value.
            ...(emailAction ? {} : { email: values.email || null }),
            ...(phoneNumberAction ? {} : { phoneNumber: values.phoneNumber || null })
          })
        }
      />

      {enablePasswordChange && <PasswordChange />}

      <MfaManagement />

      <PasskeyManagement />

      <RecoveryCodesManagement />
    </div>
  );
};

type AccountFormProps = {
  defaultValues: FormValues;
  texts: {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    save: string;
    language: string;
  };
  onSubmit: (values: FormValues) => Promise<unknown>;
  requireContactDetails: boolean;
  requiredMessage: string;
  emailAction?: ReactNode;
  phoneNumberAction?: ReactNode;
};

const AccountForm = ({
  defaultValues,
  texts,
  onSubmit,
  requireContactDetails,
  requiredMessage,
  emailAction,
  phoneNumberAction
}: AccountFormProps) => {
  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: accountValidationSchema(requireContactDetails, requiredMessage)
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    }
  });

  // The email and phone inputs are disabled while a dialog owns them, so a
  // dialog save has to be copied in or the next profile save still sees the old blank.
  useEffect(() => {
    if (emailAction !== undefined) {
      form.setFieldValue('email', defaultValues.email);
    }
    if (phoneNumberAction !== undefined) {
      form.setFieldValue('phoneNumber', defaultValues.phoneNumber);
    }
  }, [defaultValues.email, defaultValues.phoneNumber, emailAction, phoneNumberAction, form]);

  return (
    <form.AppForm>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <NoddiBasicCard>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
            <form.AppField name='firstName'>{(field) => <field.Input label={texts.firstName} />}</form.AppField>
            <form.AppField name='lastName'>{(field) => <field.Input label={texts.lastName} />}</form.AppField>
            <form.AppField name='email'>
              {(field) => (
                <div className='flex items-end gap-4'>
                  <div className='flex-1'>
                    <field.Input label={texts.email} disabled={emailAction !== undefined} />
                  </div>
                  {emailAction}
                </div>
              )}
            </form.AppField>
            <form.AppField name='phoneNumber'>
              {(field) => (
                <div className='flex items-end gap-4'>
                  <div className='flex-1'>
                    <field.Input label={texts.phoneNumber} disabled={phoneNumberAction !== undefined} />
                  </div>
                  {phoneNumberAction}
                </div>
              )}
            </form.AppField>
            <form.AppField name='languageCode'>
              {() => <TanstackLanguageCodeSelect label={texts.language} />}
            </form.AppField>
          </div>

          <div className='mt-6 flex w-full justify-end'>
            <form.SubscribeButton label={texts.save} />
          </div>
        </NoddiBasicCard>
      </form>
    </form.AppForm>
  );
};
